import { useNProgress } from '@vueuse/integrations/useNProgress'
import { createRouter, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'
import NotFound from '@/components/NotFound.vue'
import { type FatalError, useGlobalStore } from '@/store/global.ts'
import {
  isGatewayTimeoutError,
  isNetworkError,
  isServiceUnavailableError,
  isUnauthorizedError,
} from '@/utilities/axiosErrorUtils.ts'
import { AxiosError, isAxiosError } from 'axios'
import { captureException } from '@sentry/vue'
import appInsights from '@/plugins/applicationinsights'
import { useAuthStore } from '@/store/auth'
import { NavigationResult } from 'unplugin-vue-router/data-loaders'

// We generate the NotFound route ourselves to render it from within a component, following the vue-router docs at
// https://router.vuejs.org/guide/essentials/dynamic-matching.html#Catch-all-404-Not-found-Route.
routes.push({
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: NotFound,
  meta: { requiresAuth: true },
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
      }
    }
    return { top: 0 }
  },
  routes,
})

const { start, done } = useNProgress(null)

router.beforeEach(async (to, from, next) => {
  useGlobalStore().setFatalError(null)
  if (to.meta.requiresAuth !== false) {
    const authStore = useAuthStore()

    if (!authStore.isAuthEnabled) {
      start()
      authStore.initializeMock()
      next()
      return
    }

    if (!authStore.isInitialized) {
      await authStore.initialize()
    }

    if (authStore.isAuthenticated) {
      start()
      next()
      return
    }

    await authStore.login()
    next() // remove this line
  } else {
    start()
    next()
  }
})

router.afterEach(to => {
  appInsights?.trackPageView({
    name: to.name,
    uri: to.fullPath,
  })
  done()
})

router.onError(error => {
  handleError(error)
  done()
})

function handleError(error: unknown) {
  const payload: FatalError = {
    heading: 'An error occurred',
    message: '',
    identifier: undefined,
    statusCode: 500,
  }

  if (error instanceof NavigationResult && (error.value as { name: string }).name === 'NotFound') {
    payload.heading = 'Page not found'
    payload.message = `We\'re sorry, but it looks like the page you are looking for does not exist.
                       Please check the URL and try again.`
    payload.statusCode = 404
  } else if (isAxiosError(error)) {
    if (isNetworkError(error)) {
      payload.heading = 'Network Error'
      payload.message = `Please check your network connection and click the button below to
      retry or go back to the front page.`
      payload.statusCode = 500
    } else if (isGatewayTimeoutError(error)) {
      payload.heading = 'System Upgrade in Progress'
      payload.message = `Please wait a few moments, and click the button below to retry or go back to the front page.`
      payload.statusCode = 504
    } else if (isUnauthorizedError(error)) {
      payload.heading = 'Access Denied'
      payload.message = `You do not have permission to view this page.
      If you believe you should have access, please click the button below to apply.`
      payload.statusCode = 401
    } else if (isServiceUnavailableError(error)) {
      payload.heading = 'Service unavailable'
      payload.message =
        error.response?.data?.message ??
        `Please wait a few moments, and click the button below to retry or go back to the front page.`
      payload.statusCode = 503
    } else {
      payload.identifier = captureException(error, { data: (error as AxiosError)?.response?.data })
      payload.heading = 'Something went wrong'
      payload.message = `We\'re sorry, but it looks like there was a problem with this page.
      Please click the button below to retry or go back to the front page.`
      payload.statusCode = error.response?.status ?? 500
    }
  } else {
    payload.identifier = captureException(error)
    payload.heading = 'Something went wrong'
    payload.message = `We\'re sorry, but it looks like there was a problem with this page.
                       Please click the button below to retry or go back to the front page.`
  }

  useGlobalStore().setFatalError(payload)
}

export default router
